import { SizeProp } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon, FontAwesomeIconProps } from '@fortawesome/react-fontawesome';
import clsx from 'clsx';
import { CSSProperties, ForwardedRef, forwardRef, useMemo } from 'react';
import { useThemeColorPalette } from '../../hooks/use-theme-color-palette';
import { useThemeColorVar } from '../../hooks/use-theme-color-var';
import { ThemeColors, ThemeColorsValues } from '../../theme';

import styles from './icon.module.scss';

export type IconProps = Omit<FontAwesomeIconProps, 'color' | 'size' | 'ref'> & {
  /**
   * Custom color for icon
   */
  color?: ThemeColorsValues | string | 'inherit';

  /**
   * Size of the icon
   */
  size?: FontAwesomeIconProps['size'] | 'inherit';

  /**
   * Does icon have wrapper
   */
  hasWrapper?: boolean;

  classes?: {
    wrapper?: string;
    icon?: string;
  };
};

const sizeToWrapperDimensions = (size?: FontAwesomeIconProps['size'] | 'inherit') => {
  switch (size) {
    case 'xs':
      return { width: '1.5rem', height: '1.5rem' };
    case 'sm':
      return { width: '2rem', height: '2rem' };
    case 'lg':
      return { width: '3rem', height: '3rem' };
    case '2x':
      return { width: '4rem', height: '4rem' };
    case '3x':
      return { width: '6rem', height: '6rem' };
    case '4x':
      return { width: '8rem', height: '8rem' };
    case '5x':
      return { width: '10rem', height: '10rem' };
    case '6x':
      return { width: '12rem', height: '12rem' };
    case '7x':
      return { width: '14rem', height: '14rem' };
    case '8x':
      return { width: '16rem', height: '16rem' };
    case '9x':
      return { width: '18rem', height: '18rem' };
    case '10x':
      return { width: '20rem', height: '20rem' };
    case 'inherit':
      return {};
    default:
      return { width: '2rem', height: '2rem' };
  }
};

export const Icon = forwardRef<SVGSVGElement, IconProps>(
  ({ color, size, hasWrapper = false, classes: customClasses, ...props }, ref: ForwardedRef<SVGSVGElement>) => {
    const c = useThemeColorVar(color);
    const palette = useThemeColorPalette(color as ThemeColors);

    const wrapperStyle = useMemo(() => {
      return {
        ...sizeToWrapperDimensions(size),
        '--icon--wrapper-color': palette?.['ultra-light'] ?? undefined,
      };
    }, [size, palette]);

    const iconElement = (
      <FontAwesomeIcon
        ref={ref}
        style={{ '--icon--color': c ?? undefined } as CSSProperties}
        size={size === 'inherit' ? undefined : size as SizeProp}
        {...props}
        className={clsx(props.className, styles.icon, customClasses?.icon)}
      />
    );

    return hasWrapper ? (
      <div
        className={clsx(styles['icon-wrapper'], customClasses?.wrapper)}
        style={wrapperStyle}
      >
        {iconElement}
      </div>
    ) : (
      iconElement
    );
  }
);
